/**
 * @page /portal/address-clarifications
 * @description Kunden-Portal: Adressklärungen — read-only Tabelle aller
 *   Adressklärungsfälle des Kunden.
 * @api GET /api/portal/my-address-clarifications
 * @roles kunde
 * @i18n portal.addressClarifications
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_CONFIG = {
  offen:          { bg: 'bg-blue-500/10',    text: 'text-blue-600 dark:text-blue-400',    dot: 'bg-blue-500',    key: 'portal.addressClarifications.status.open' },
  email_gesendet: { bg: 'bg-violet-500/10',  text: 'text-violet-600 dark:text-violet-400', dot: 'bg-violet-500',  key: 'portal.addressClarifications.status.emailSent' },
  beantwortet:    { bg: 'bg-cyan-500/10',    text: 'text-cyan-600 dark:text-cyan-400',    dot: 'bg-cyan-500',    key: 'portal.addressClarifications.status.answered' },
  abgeschlossen:  { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', key: 'portal.addressClarifications.status.closed' },
};

function StatusBadge({ status, t }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.offen;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {t(cfg.key)}
    </span>
  );
}

export default function PortalAddressClarifications() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/portal/my-address-clarifications`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(d => setData(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={t('portal.addressClarifications.title')} subtitle={t('portal.addressClarifications.subtitle')} />

      {/* Info Box */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {t('portal.addressClarifications.infoText')}
        </p>
      </div>

      {/* Table */}
      {data.length > 0 ? (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.addressClarifications.date')}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.addressClarifications.orderNumber')}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.addressClarifications.recipient')}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.addressClarifications.problem')}</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.addressClarifications.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr key={row.id} className="border-b border-line last:border-0 hover:bg-surface-hover transition-colors">
                    <td className="px-4 py-3 text-xs text-[var(--text-secondary)] whitespace-nowrap">
                      {new Date(row.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-content font-mono">
                      {row.bestellung_nr || '\u2013'}
                    </td>
                    <td className="px-4 py-3 text-xs text-content">
                      <div>{row.empfaenger || '\u2013'}</div>
                      {(row.plz || row.ort) && (
                        <div className="text-[10px] text-[var(--text-tertiary)]">{[row.plz, row.ort].filter(Boolean).join(' ')}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--text-secondary)] max-w-[250px]">
                      <span className="line-clamp-2">{row.problem_beschreibung || '\u2013'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={row.status} t={t} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-surface-card border border-line rounded-card p-10 text-center">
          <p className="text-sm text-[var(--text-secondary)]">{t('portal.addressClarifications.noEntries')}</p>
        </div>
      )}
    </div>
  );
}
